<script setup lang="ts">
import { computed, h, onMounted, ref, watch, type VNodeRef } from 'vue';
import {
  NButton,
  NColorPicker,
  NIcon,
  NPopconfirm,
  NSpace,
  NTag,
  type DataTableColumns,
} from 'naive-ui';
import { IconAdd, IconCheck, IconClose, IconDelete } from '@/components/design/icons';
import type { ReviewProjectInDb, ReviewProjectTagInDb, UserInDb } from '@/api';
import GaiaTable from '@/components/design/base/GaiaTable.vue';
import ReviewProjectExternalAccess from '@/components/reviewProject/ReviewProjectExternalAccess.vue';
import UserInitialsAvatar from '@/components/UserInitialsAvatar.vue';
import { ReviewProjectService } from '@/services/reviewProjectService';
import { tagChipColor } from '@/utils/tagColors';
import GaiaUserPickerList from '@/components/design/domain/people/GaiaUserPickerList.vue';
import type { GaiaPickerListInstance } from '@/components/design/domain/people/types';
import GaiaCheckbox from '@/components/design/base/GaiaCheckbox.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';

const props = defineProps<{
  project: ReviewProjectInDb;
}>();

const emit = defineEmits<{
  (e: 'updated', project: ReviewProjectInDb): void;
}>();

const message = useGaiaToast();

// ==================== Name ====================
const name = ref<string>(props.project.name);
const savingName = ref<boolean>(false);

watch(
  () => props.project.name,
  value => {
    name.value = value;
  }
);

const nameChanged = computed(
  () => name.value.trim().length > 0 && name.value.trim() !== props.project.name
);

async function handleSaveName(): Promise<void> {
  savingName.value = true;
  try {
    const updated = await ReviewProjectService.update(props.project.uid, {
      name: name.value.trim(),
    });
    message.success('Project renamed successfully');
    emit('updated', updated);
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to rename project.');
  } finally {
    savingName.value = false;
  }
}

// ==================== Allow duplicate runs ====================
const allowDuplicateRuns = ref<boolean>(props.project.allow_duplicate_runs);
const savingAllowDuplicates = ref<boolean>(false);

watch(
  () => props.project.allow_duplicate_runs,
  value => {
    allowDuplicateRuns.value = value;
  }
);

async function handleToggleAllowDuplicates(value: boolean): Promise<void> {
  allowDuplicateRuns.value = value;
  savingAllowDuplicates.value = true;
  try {
    const updated = await ReviewProjectService.update(props.project.uid, {
      allow_duplicate_runs: value,
    });
    emit('updated', updated);
  } catch (error) {
    allowDuplicateRuns.value = props.project.allow_duplicate_runs;
    message.error(error instanceof Error ? error.message : 'Failed to update project.');
  } finally {
    savingAllowDuplicates.value = false;
  }
}

// ==================== Members ====================
const members = ref<UserInDb[]>([]);
const membersLoading = ref<boolean>(false);

async function fetchMembers(): Promise<void> {
  membersLoading.value = true;
  try {
    members.value = await ReviewProjectService.listMembers(props.project.uid);
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to load members.');
  } finally {
    membersLoading.value = false;
  }
}

function isOwner(user: UserInDb): boolean {
  return user.uid === props.project.owner_user?.uid;
}

async function handleRemoveMember(user: UserInDb): Promise<void> {
  try {
    await ReviewProjectService.removeMember(props.project.uid, user.uid);
    message.success(`Removed ${user.full_name || user.email}`);
    await fetchMembers();
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to remove member.');
  }
}

// ==================== Add Members Modal ====================
const showAddModal = ref<boolean>(false);
const selectedUserUids = ref<string[]>([]);
const adding = ref<boolean>(false);
const addListRef = ref<GaiaPickerListInstance | null>(null);

/**
 * Existing members are shown disabled rather than filtered out: hiding them
 * made a search for someone already on the project look like they did not
 * exist, and the list pages server-side now, so a hidden row would also throw
 * the page counts off.
 */
const memberUids = computed(() => members.value.map(user => user.uid));

function openAddModal(): void {
  selectedUserUids.value = [];
  showAddModal.value = true;
  addListRef.value?.reset();
}

async function handleAddSelected(): Promise<void> {
  const uids = selectedUserUids.value;
  if (uids.length === 0) return;

  adding.value = true;
  try {
    for (const uid of uids) {
      await ReviewProjectService.addMember(props.project.uid, uid);
    }
    message.success(`Added ${uids.length} member${uids.length === 1 ? '' : 's'}`);
    showAddModal.value = false;
    await fetchMembers();
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to add members.');
    await fetchMembers();
  } finally {
    adding.value = false;
  }
}

// ==================== Tags ====================
// Shared with the column render fns, where scoped CSS classes don't apply.
const tagSwatchStyle = {
  width: '22px',
  height: '22px',
  borderRadius: '50%',
  border: '1px solid rgba(128, 128, 128, 0.35)',
  cursor: 'pointer',
};

interface TagRow {
  tag: ReviewProjectTagInDb;
  draftName: string;
  draftColor: string;
  editingName: boolean;
  saving: boolean;
}

const TAGS_PAGE_SIZE = 50;
const tagRows = ref<TagRow[]>([]);
const tagsLoading = ref<boolean>(false);
const tagsPage = ref<number>(1);
const tagsCount = ref<number>(0);

const tagsPagination = computed(() => ({
  page: tagsPage.value,
  pageSize: TAGS_PAGE_SIZE,
  itemCount: tagsCount.value,
  onUpdatePage: (page: number): void => {
    tagsPage.value = page;
    void fetchTags();
  },
}));

function isTagDirty(row: TagRow): boolean {
  return row.draftName.trim() !== row.tag.name || row.draftColor !== row.tag.color;
}

async function fetchTags(): Promise<void> {
  tagsLoading.value = true;
  try {
    const result = await ReviewProjectService.searchTags(props.project.uid, {
      page: tagsPage.value,
      pageSize: TAGS_PAGE_SIZE,
    });
    tagsCount.value = result.result_count;
    tagRows.value = result.results.map(tag => ({
      tag,
      draftName: tag.name,
      draftColor: tag.color,
      editingName: false,
      saving: false,
    }));
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to load tags.');
  } finally {
    tagsLoading.value = false;
  }
}

async function handleSaveTag(row: TagRow): Promise<void> {
  if (!isTagDirty(row) || row.draftName.trim().length === 0) return;
  row.saving = true;
  try {
    const updated = await ReviewProjectService.updateTag(props.project.uid, row.tag.uid, {
      name: row.draftName.trim(),
      color: row.draftColor,
    });
    row.tag = updated;
    row.draftName = updated.name;
    row.draftColor = updated.color;
    row.editingName = false;
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to update tag.');
  } finally {
    row.saving = false;
  }
}

async function handleDeleteTag(row: TagRow): Promise<void> {
  try {
    await ReviewProjectService.deleteTag(props.project.uid, row.tag.uid);
    message.success(`Deleted tag "${row.tag.name}"`);
    await fetchTags();
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to delete tag.');
  }
}

const newTagName = ref<string>('');
const newTagColor = ref<string>('#2080f0');
const creatingTag = ref<boolean>(false);

async function handleCreateTag(): Promise<void> {
  const name = newTagName.value.trim();
  if (name.length === 0) return;
  creatingTag.value = true;
  try {
    await ReviewProjectService.createTag(props.project.uid, {
      name,
      color: newTagColor.value,
    });
    newTagName.value = '';
    await fetchTags();
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to create tag.');
  } finally {
    creatingTag.value = false;
  }
}

const tagColumns: DataTableColumns<TagRow> = [
  {
    title: 'Color',
    key: 'color',
    width: 64,
    render: row =>
      h(
        NColorPicker,
        {
          value: row.draftColor,
          showAlpha: false,
          modes: ['hex'],
          style: 'width: 22px',
          onUpdateValue: (value: string): void => {
            row.draftColor = value;
          },
        },
        {
          trigger: ({
            value,
            onClick,
            ref: triggerRef,
          }: {
            value: string | null;
            onClick: () => void;
            ref: VNodeRef;
          }) =>
            h('div', {
              ref: triggerRef,
              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
              style: { ...tagSwatchStyle, backgroundColor: value || '#dbeafe' },
              onClick,
            }),
        }
      ),
  },
  {
    title: 'Name',
    key: 'name',
    render: row =>
      row.editingName
        ? h(GaiaInput, {
            value: row.draftName,
            size: 'small',
            placeholder: 'Tag name',
            ref: (inst: unknown): void => {
              (inst as { focus?: () => void } | null)?.focus?.();
            },
            onUpdateValue: (value: string): void => {
              row.draftName = value;
            },
            onBlur: (): void => {
              if (!isTagDirty(row)) row.editingName = false;
            },
            onKeydown: (e: KeyboardEvent): void => {
              if (e.key === 'Enter') {
                if (isTagDirty(row)) void handleSaveTag(row);
                else row.editingName = false;
              } else if (e.key === 'Escape') {
                row.draftName = row.tag.name;
                row.editingName = false;
              }
            },
          })
        : h(
            NTag,
            {
              size: 'small',
              round: true,
              color: tagChipColor(row.draftColor),
              style: 'cursor: pointer',
              onClick: (): void => {
                row.editingName = true;
              },
            },
            { default: () => row.draftName }
          ),
  },
  {
    title: '',
    key: 'actions',
    width: 88,
    render: row =>
      h(
        NSpace,
        { justify: 'end', size: 4, wrap: false },
        {
          default: () => [
            isTagDirty(row)
              ? h(
                  NButton,
                  {
                    quaternary: true,
                    circle: true,
                    size: 'small',
                    type: 'primary',
                    loading: row.saving,
                    disabled: row.draftName.trim().length === 0,
                    onClick: (): void => void handleSaveTag(row),
                  },
                  { icon: () => h(NIcon, null, { default: () => h(IconCheck) }) }
                )
              : null,
            h(
              NPopconfirm,
              { onPositiveClick: (): void => void handleDeleteTag(row) },
              {
                trigger: () =>
                  h(
                    NButton,
                    { quaternary: true, circle: true, size: 'small' },
                    { icon: () => h(NIcon, null, { default: () => h(IconDelete) }) }
                  ),
                default: () => 'Delete this tag? It will be removed from all files.',
              }
            ),
          ],
        }
      ),
  },
];

onMounted(() => {
  void fetchMembers();
  void fetchTags();
});
</script>

<template>
  <n-space vertical :size="16" style="max-width: 640px; padding: 10px">
    <n-card title="General" size="small">
      <n-space vertical :size="12">
        <n-form-item label="Name" :show-feedback="false">
          <n-flex :size="8" :wrap="false" style="width: 100%">
            <gaia-input v-model:value="name" placeholder="Project name" style="flex: 1" />
            <n-button
              type="primary"
              :disabled="!nameChanged"
              :loading="savingName"
              @click="handleSaveName"
            >
              Save
            </n-button>
          </n-flex>
        </n-form-item>

        <gaia-checkbox
          :checked="allowDuplicateRuns"
          :disabled="savingAllowDuplicates"
          @update:checked="handleToggleAllowDuplicates"
        >
          Allow duplicate playbook runs
        </gaia-checkbox>
      </n-space>
    </n-card>

    <n-card title="Members" size="small" data-tour="rp.settings-members">
      <template #header-extra>
        <n-button size="small" @click="openAddModal">
          <template #icon>
            <n-icon><IconAdd /></n-icon>
          </template>
          Add members
        </n-button>
      </template>

      <n-spin :show="membersLoading">
        <n-list v-if="members.length > 0" bordered>
          <n-list-item v-for="member in members" :key="member.uid">
            <n-flex align="center" :size="12" :wrap="false">
              <UserInitialsAvatar :user="member" :show-tooltip="false" />
              <n-flex vertical :size="0">
                <n-text>{{ member.full_name || member.email }}</n-text>
                <n-text v-if="member.full_name" :depth="3" style="font-size: 12px">
                  {{ member.email }}
                </n-text>
              </n-flex>
            </n-flex>
            <template #suffix>
              <n-tag v-if="isOwner(member)" size="small" type="info">Owner</n-tag>
              <n-button v-else quaternary circle size="small" @click="handleRemoveMember(member)">
                <template #icon>
                  <n-icon><IconClose /></n-icon>
                </template>
              </n-button>
            </template>
          </n-list-item>
        </n-list>
        <n-empty v-else-if="!membersLoading" description="No members yet" />
      </n-spin>
    </n-card>

    <n-card title="External access" size="small">
      <ReviewProjectExternalAccess :project="project" @updated="emit('updated', $event)" />
    </n-card>

    <n-card title="Tags" size="small">
      <n-space vertical :size="12">
        <n-flex :size="8" :wrap="false" align="center">
          <gaia-input
            v-model:value="newTagName"
            size="small"
            placeholder="New tag name"
            style="flex: 1; min-width: 150px"
            @keydown.enter="handleCreateTag"
          />
          <n-color-picker
            v-model:value="newTagColor"
            :show-alpha="false"
            :modes="['hex']"
            style="width: 22px"
          >
            <template #trigger="{ value, onClick, ref: triggerRef }">
              <div
                :ref="triggerRef"
                :style="{ ...tagSwatchStyle, backgroundColor: value || '#dbeafe' }"
                @click="onClick"
              />
            </template>
          </n-color-picker>
          <n-button
            size="small"
            :disabled="newTagName.trim().length === 0"
            :loading="creatingTag"
            @click="handleCreateTag"
          >
            <template #icon>
              <n-icon><IconAdd /></n-icon>
            </template>
            Add
          </n-button>
        </n-flex>
        <gaia-table
          remote
          :columns="tagColumns"
          :data="tagRows"
          :loading="tagsLoading"
          :row-key="(row: TagRow) => row.tag.uid"
          :pagination="tagsPagination"
        />
      </n-space>
    </n-card>
  </n-space>

  <!-- Outside the n-space: a teleported modal would otherwise leave an empty gap item. -->
  <n-modal
    v-model:show="showAddModal"
    preset="card"
    title="Add members"
    style="width: 640px"
    :mask-closable="false"
  >
    <gaia-user-picker-list
      ref="addListRef"
      v-model:value="selectedUserUids"
      multiple
      :page-size="10"
      :disabled-uids="memberUids"
    />
    <template #footer>
      <n-space justify="end">
        <n-button @click="showAddModal = false">Cancel</n-button>
        <n-button
          type="primary"
          :disabled="selectedUserUids.length === 0"
          :loading="adding"
          @click="handleAddSelected"
        >
          Add selected ({{ selectedUserUids.length }})
        </n-button>
      </n-space>
    </template>
  </n-modal>
</template>
